import React, { useState, useMemo, useCallback } from 'react';
import { FiCalendar, FiX, FiCheck, FiSearch, FiArrowRight } from 'react-icons/fi';
import styled, { keyframes } from 'styled-components';
import { addDays } from 'date-fns';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';
import { useModal } from '../../../../../../hooks/Modal';
import { useAuth } from '../../../../../../hooks/Auth';
import { useSearch } from '../../../../../../hooks/Search';

// =========================================================
// Endpoints
// Obs.: você mencionou "/sales-invoices" (plural), mas todo o módulo
// hoje usa "/sales-invoice" (singular) — GET do ReprocessInvoices,
// endpoint do SearchComponent e os PUTs por campo. Mantive o singular
// pra bater com o que já funciona. Se a rota de listagem for mesmo
// plural, troque só a constante abaixo.
// =========================================================
const LIST_ENDPOINT = '/sales-invoice';
const PUT_ENDPOINT = '/sales-invoice';

// =========================================================
// Tipos
// =========================================================
interface Invoice {
  _id: string;
  installment_number: number;
  payment_date: string;
  installment_value: number;
  payment_status_id: string;      // 'paid' | 'waiting' | 'cancelled' | ...
  payment_method_id: string;
  invoice_hash: string;
  project_id?: string;
  client_id?: string;
  client_id_response?: { name?: string };
}

interface Row {
  invoice: Invoice;
  currentDate: Date;
  proposedDate: Date;
}

type ShiftDays = 1 | 2 | 3;

// =========================================================
// Helpers
// =========================================================
const formatBRL = (cents: number) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

// dd/mm/yyyy a partir dos componentes UTC (as faturas gravam meia-noite
// BRT como 03:00Z, então UTC preserva o dia correto em qualquer fuso).
const formatDateBR = (d: Date | string | null) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const iso = dt.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
  const [y, m, day] = iso.split('-');
  return `${day}/${m}/${y}`;
};

// YYYY-MM-DD (UTC) — usado para casar a data escolhida com a da fatura.
const toUTCDateStr = (d: Date | string) => {
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
};

// =========================================================
// Styled
// =========================================================
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

const Container = styled.div`
  background: #fff;
  border-radius: 10px;
  min-width: 360px;
  max-width: 920px;
  width: 100%;
  color: #1f2937;
  font-family: 'Inter', -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;
const Header = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 12px;
`;
const HeaderTitle = styled.div`
  flex: 1;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;
const HeaderClose = styled.button`
  background: none; border: none; cursor: pointer; color: #6b7280;
  display: inline-flex; align-items: center;
  &:hover { color: var(--adminColor); }
`;
const Body = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
`;
const Footer = styled.div`
  padding: 14px 22px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
`;
const Filters = styled.div`
  display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; margin-bottom: 16px;
  .field { display: flex; flex-direction: column; gap: 5px; }
  label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
  input {
    height: 40px; padding: 0 12px; border: 1.5px solid #e5e7eb; border-radius: 8px;
    font-size: 13px; font-family: inherit; background: #fff;
    &:focus { outline: none; border-color: var(--adminColor); }
  }
`;
const BtnSearch = styled.button`
  height: 40px; padding: 0 16px; border: none; border-radius: 8px; cursor: pointer;
  background: var(--adminColor); color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const ShiftBar = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
  span.lbl { font-size: 12px; color: #6b7280; font-weight: 600; }
  span.count { margin-left: auto; font-size: 12px; color: #6b7280; }
`;
const SegSwitch = styled.div`
  display: inline-flex; border: 1.5px solid #e5e7eb; border-radius: 8px; overflow: hidden;
  button {
    border: none; background: #fff; cursor: pointer; padding: 8px 14px; font-size: 12px;
    font-weight: 600; color: #6b7280; font-family: inherit;
    & + button { border-left: 1.5px solid #e5e7eb; }
    &.active { background: var(--adminColor); color: #fff; }
    &:disabled { opacity: .5; cursor: not-allowed; }
  }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
  th {
    text-align: left; padding: 10px 8px; font-size: 11px; font-weight: 600;
    color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em;
    border-bottom: 1px solid #e5e7eb; background: #fafafa;
  }
  td { padding: 10px 8px; border-bottom: 1px solid #f3f4f6; color: #1f2937; vertical-align: middle; }
  .num { font-variant-numeric: tabular-nums; text-align: right; }
  .arrow { color: var(--adminColor); }
  .old { color: #9ca3af; text-decoration: line-through; }
  .new { color: var(--adminColor); font-weight: 700; }
  .muted { color: #9ca3af; font-size: 11px; }
`;
const Empty = styled.div`
  text-align: center; padding: 40px 0; color: #9ca3af; font-size: 13px;
`;
const Spinner = styled.div`
  width: 18px; height: 18px; border: 2px solid #e5e7eb; border-top-color: var(--adminColor);
  border-radius: 999px; animation: ${spin} 1s linear infinite; display: inline-block;
`;
const ProgressInfo = styled.div`
  font-size: 12px; color: #6b7280; flex: 1;
  strong { color: var(--adminColor); font-weight: 700; }
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 16px; border: 1.5px solid #e5e7eb; border-radius: 8px; cursor: pointer;
  background: #fff; color: #6b7280; font-size: 13px; font-weight: 600;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border: none; border-radius: 8px; cursor: pointer;
  background: var(--adminColor); color: #fff; font-size: 13px; font-weight: 700;
  display: inline-flex; align-items: center; gap: 6px;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

// =========================================================
// Flow
// =========================================================
const PostponeFlow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const { reloadSearchAll } = useSearch();

  const [dueDate, setDueDate] = useState<string>(''); // 'YYYY-MM-DD' vindo do input date
  const [shiftDays, setShiftDays] = useState<ShiftDays>(1);

  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [searched, setSearched] = useState(false);

  // Faturas 'waiting' (todos os projetos) que vencem na data escolhida
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // ----- buscar faturas -----
  const search = useCallback(async () => {
    if (!dueDate) {
      addToast({ title: 'Selecione a data de vencimento', type: 'error' });
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(LIST_ENDPOINT, {
        params: { limitItems: 1000000, where: { payment_status_id: 'waiting' } },
      });
      const list: Invoice[] = (data?.rows || []).filter((inv: Invoice) => {
        if (inv.payment_status_id !== 'waiting') return false; // reforço no front
        if (!inv.payment_date) return false;
        return toUTCDateStr(inv.payment_date) === dueDate;
      });
      setInvoices(list);
    } catch (err) {
      addToast({ title: 'Erro ao carregar faturas', type: 'error' });
      handleApiErrors(err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [dueDate, addToast, handleApiErrors]);

  // linhas + data proposta (recalcula ao trocar o nº de dias)
  const rows: Row[] = useMemo(() => {
    return invoices
      .map(i => {
        const currentDate = new Date(i.payment_date);
        return { invoice: i, currentDate, proposedDate: addDays(currentDate, shiftDays) };
      })
      .sort((a, b) =>
        (a.invoice.client_id_response?.name || '').localeCompare(b.invoice.client_id_response?.name || ''),
      );
  }, [invoices, shiftDays]);

  const totalCents = useMemo(
    () => rows.reduce((acc, r) => acc + (r.invoice.installment_value || 0), 0),
    [rows],
  );

  // ----- aplicar: 1 PUT por fatura, individualmente -----
  const apply = async () => {
    if (rows.length === 0) return;
    setApplying(true);
    setProgress({ done: 0, total: rows.length });
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < rows.length; i++) {
      const { invoice, proposedDate } = rows[i];
      try {
        await api.put(`${PUT_ENDPOINT}/${invoice._id}/payment_date`, {
          _id: invoice._id,
          payment_date: proposedDate,
        });
        ok++;
      } catch {
        fail++; // segue para a próxima fatura
      }
      setProgress({ done: i + 1, total: rows.length });
    }
    setApplying(false);
    addToast({
      title: `${ok} fatura(s) adiada(s) em ${shiftDays} dia(s)` + (fail ? ` · ${fail} com erro` : ''),
      type: fail ? 'error' : 'success',
    });
    reloadSearchAll();
    await search(); // recarrega: as adiadas somem desta data
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <h3>Adiar vencimentos</h3>
          <p>Lista as faturas aguardando de uma data (todos os projetos) e empurra o vencimento em 1, 2 ou 3 dias.</p>
        </HeaderTitle>
        <HeaderClose onClick={onClose} disabled={applying}><FiX size={18} /></HeaderClose>
      </Header>

      <Body>
        <Filters>
          <div className="field">
            <label>Data de vencimento</label>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') search(); }}
              disabled={loading || applying}
            />
          </div>
          <BtnSearch onClick={search} disabled={loading || applying}>
            <FiSearch size={14} /> Buscar
          </BtnSearch>
        </Filters>

        {loading ? (
          <Empty><Spinner /> <div style={{ marginTop: 8 }}>Carregando…</div></Empty>
        ) : !searched ? (
          <Empty>Selecione a data de vencimento e clique em Buscar.</Empty>
        ) : rows.length === 0 ? (
          <Empty>Nenhuma fatura aguardando com vencimento em {formatDateBR(dueDate)}.</Empty>
        ) : (
          <>
            <ShiftBar>
              <span className="lbl">Adiar em:</span>
              <SegSwitch>
                {[1, 2, 3].map(n => (
                  <button
                    key={n}
                    className={shiftDays === n ? 'active' : ''}
                    onClick={() => setShiftDays(n as ShiftDays)}
                    disabled={applying}
                  >
                    {n} dia{n > 1 ? 's' : ''}
                  </button>
                ))}
              </SegSwitch>
              <span className="count">{rows.length} fatura(s) · R$ {formatBRL(totalCents)}</span>
            </ShiftBar>

            <Table>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Cliente</th>
                  <th>Projeto</th>
                  <th>Vencimento atual</th>
                  <th></th>
                  <th>Novo vencimento</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.invoice._id}>
                    <td>#{r.invoice.installment_number}</td>
                    <td>{r.invoice.client_id_response?.name || '—'}</td>
                    <td className="muted">{r.invoice.project_id || '—'}</td>
                    <td className="old">{formatDateBR(r.currentDate)}</td>
                    <td className="arrow"><FiArrowRight size={13} /></td>
                    <td className="new">{formatDateBR(r.proposedDate)}</td>
                    <td className="num">R$ {formatBRL(r.invoice.installment_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Body>

      {searched && rows.length > 0 && (
        <Footer>
          {applying ? (
            <ProgressInfo>
              <Spinner style={{ verticalAlign: 'middle', marginRight: 6, width: 14, height: 14 }} />
              Atualizando <strong>{progress.done}</strong> de <strong>{progress.total}</strong>…
            </ProgressInfo>
          ) : <div style={{ flex: 1 }} />}
          <BtnCancel onClick={onClose} disabled={applying}>Cancelar</BtnCancel>
          <BtnPrimary onClick={apply} disabled={applying}>
            <FiCheck size={14} />
            Confirmar · adiar {rows.length} em {shiftDays} dia(s)
          </BtnPrimary>
        </Footer>
      )}
    </Container>
  );
};

// =========================================================
// Botão exportado (vai no headModules da SalesInvoice)
// =========================================================
export default function PostponeInvoicesModule(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'postpone-invoices',
      title: '',
      theme: 'whiteModal',
      content: <PostponeFlow onClose={() => removeModal('postpone-invoices')} />,
    });
  };

  return (
    <button
      className="searchButton"
      title="Adiar vencimento de faturas por data"
      type="button"
      onClick={open}
    >
      <FiCalendar />
    </button>
  );
}
